import contact from 'api/contact'
import { Formik } from 'formik'
import { useCallback, useRef, useState } from 'react'
import * as yup from 'yup'
import ReCAPTCHA from 'react-google-recaptcha'

import { useTranslation } from 'next-i18next'

import Button from 'components/Button'
import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import Field from 'components/Form/Field/Field'
import Form from 'components/Form/Form'
import usePlausible from 'utils/hooks/usePlausible'
import { PlausibleEvent } from 'enums/plausible'
import validateMessage from './validateMessage'
import handleResponseError from './handleResponseError'
import ErrorCode from './ErrorCodes'

import styles from './ContactsForm.module.scss'

const SuccessPopup = ({ onClick }) => {
  const [t] = useTranslation(['contacts'])
  return (
    <div className={styles.popup_wrapper}>
      <div className={styles.popup}>
        <div className={styles.mailbox}>
          <img width="100px" src="/images/mailbox.png" alt="mailbox" />
        </div>
        <div className={styles.title}>{t('contacts:yourMessageSent')}</div>
        <div className={styles.subtitle}>{t('contacts:thanksForQ')}</div>
        <div className={styles.repeat}>
          <Button onClick={onClick}>{t('contacts:writeAgain')}</Button>
        </div>
      </div>
    </div>
  )
}

const ContactsForm = ({ referrer }) => {
  const setEvent = usePlausible()
  const [t] = useTranslation(['error', 'contacts'])

  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const captchaRef = useRef(null)

  const onSubmit = useCallback(async (values, actions) => {
    try {
      const captcha = await captchaRef.current.executeAsync()

      const res = await contact({ ...values, captcha })
      if (res.status !== 204) {
        const { code, payload } = res.data
        switch (code) {
          case ErrorCode.VALIDATION_ERRORS:
            if (payload?.errors) {
              const wrappedErrors = handleResponseError(res)
              setError(wrappedErrors)
            }
            break
          default:
            setError(validateMessage.serverError)
        }
      } else {
        setError('')
        setSubmitted(true)
        setEvent(PlausibleEvent.SentContactsMessage, { step: 2, referrer })
        actions.resetForm()
      }
    } catch (e) {
      setError(validateMessage.serverError)
    }
  }, [])
  const resetForm = useCallback(() => {
    setSubmitted(false)
  }, [setSubmitted])
  return (
    <div className={styles.wrapper}>
      {submitted && <SuccessPopup onClick={resetForm} />}
      <Formik
        enableReinitialize
        initialValues={{
          name: '',
          email: '',
          theme: '',
          message: '',
        }}
        validationSchema={yup.object({
          name: yup
            .string()
            .max(255, t('error:maxLenSymbols', { length: 255 }))
            .required(t('error:requiredField')),
          email: yup
            .string()
            .max(255, t('error:maxLenSymbols', { length: 255 }))
            .email(t('error:emailNotValid'))
            .required(t('error:requiredField')),
          theme: yup
            .string()
            .max(255, t('error:maxLenSymbols', { length: 255 }))
            .required(t('error:requiredField')),
          message: yup
            .string()
            .max(512, t('error:maxLenSymbols', { length: 512 }))
            .required(t('error:requiredField')),
        })}
        onSubmit={onSubmit}
      >
        <Form className={styles.contactsForm}>
          <Field
            withHorizontalTooltip={false}
            placeholder={t('main:optionsName')}
            type="text"
            name="name"
            id="about-contacts-name-input"
          />
          <Field
            withHorizontalTooltip={false}
            placeholder={t('main:recoveryEmail')}
            type="email"
            name="email"
            id="about-contacts-email-input"
          />
          <Field
            withHorizontalTooltip={false}
            className={styles.double}
            placeholder={t('contacts:theme')}
            type="text"
            name="theme"
            id="about-contacts-theme-input"
          />
          <Field
            withHorizontalTooltip={false}
            className={styles.double}
            placeholder={t('contacts:maxSym512')}
            type="text"
            component="textarea"
            name="message"
            id="about-contacts-message-input"
          />
          <p className={styles.captcha}>
            {t('contacts:reCaptcha')}
            <a href="https://policies.google.com/privacy" id="about-contacts-privacy-link">
              {t('main:signUpConfirmSignUpFouth')}
            </a>{' '}
            {t('contacts:and')}
            <a href="https://policies.google.com/terms" id="about-contacts-terms-link">
              {t('contacts:termsOfService')}
            </a>
            {t('contacts:apply')}
          </p>
          {error && <ErrorMessage className={styles.error} message={error} />}
          <Button className={styles.submit} type="submit" id="about-contacts-submit-button">
            {t('main:recoverySend')}
          </Button>
        </Form>
      </Formik>

      <ReCAPTCHA sitekey={process.env.NEXT_PUBLIC_RECAPTCHA} ref={captchaRef} size="invisible" />
    </div>
  )
}

export default ContactsForm
